import { gql, useQuery } from '@apollo/client';
import { Button, Tooltip } from '@apollo/orbit';
import React, { useMemo } from 'react';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';

import { LazyUrlModal } from 'src/components/common/LazyUrlModal';
import {
  UseNewFedNextVersionAvailableFragment,
  useNewFedNextVersionAvailable,
} from 'src/hooks/useNewFedNextVersionAvailable/useNewFedNextVersionAvailable';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { Config } from 'src/lib/config/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { BuildPipelineTrack, GraphType } from 'src/lib/graphqlTypes/types';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { useGetDefaultBuildPipelineTrackEnum } from '../../variantSettingsPage/variantSettingsCards/BuildConfigCard';
import {
  ConfigureBuildPipelineModal,
  configureBuildPipelineModalFragment,
} from '../../variantSettingsPage/variantSettingsCards/configureBuildPipelineModal/ConfigureBuildPipelineModal';
import { newMinorVersionAvailable } from '../../variantSettingsPage/variantSettingsCards/FederationVersionInfo';

export const UpgradeFederationButton = ({
  graphRef,
  canManageBuildConfig,
}: {
  graphRef: { graphId: string; graphVariant: string };
  canManageBuildConfig: boolean;
}) => {
  const location = useLocation();
  const { getDefaultBuildPipelineTrackEnum } =
    useGetDefaultBuildPipelineTrackEnum();
  const newFedNextVersionAvailable = useNewFedNextVersionAvailable();

  const {
    data: graphData,
    loading: graphLoading,
    error: graphLoadingError,
    refetch,
  } = useQuery<
    GraphQLTypes.UpgradeFederationButtonGraphQuery,
    GraphQLTypes.UpgradeFederationButtonGraphQueryVariables
  >(
    gql`
      query UpgradeFederationButtonGraphQuery(
        $graphId: ID!
        $graphVariant: String!
      ) {
        service(id: $graphId) {
          id
          graphType
          variant(name: $graphVariant) {
            id
            proposal {
              id
              sourceVariant {
                id
                name
                activeSchemaPublish {
                  id
                  compositionResult {
                    graphCompositionID
                  }
                }
                compositionVersion
                router {
                  id
                  routerVersion {
                    core
                  }
                }
                supportedDirectives {
                  name
                  enabled
                }
                allowedTracks {
                  badge
                  buildPipelineTrack
                  compositionVersion
                  deprecatedAt
                  displayName
                  minimumGatewayVersion
                  minimumRouterVersion
                  notSupportedAt
                }
                buildConfig {
                  buildPipelineTrack
                  tagInApiSchema
                }
                ...UseNewFedNextVersionAvailableFragment
              }
            }
            compositionVersion
            ...ConfigureBuildPipelineModalFragment
          }
        }
      }
      ${configureBuildPipelineModalFragment}
      ${UseNewFedNextVersionAvailableFragment}
    `,
    {
      variables: graphRef,
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
      skip: !canManageBuildConfig,
    },
  );

  const proposalVariant = graphData?.service?.variant;
  const proposal = proposalVariant?.proposal;
  const sourceVariant = proposal?.sourceVariant;
  const graphType = graphData?.service?.graphType || GraphType.CLASSIC;
  const routerVersion = sourceVariant?.router?.routerVersion?.core;
  const tagEnabled = !!sourceVariant?.supportedDirectives?.find(
    (directive) => directive.name === 'tag' && directive.enabled,
  );
  const defaultBuildPipelineTrackEnum = getDefaultBuildPipelineTrackEnum(
    proposalVariant?.compositionVersion,
    tagEnabled,
  );
  const allowedTracksNonNull = useMemo(
    () =>
      (sourceVariant?.allowedTracks || []).filter(
        (t): t is NonNullable<typeof t> => !!t,
      ),
    [sourceVariant],
  );
  const isUsingFedNext =
    sourceVariant?.compositionVersion === BuildPipelineTrack.FED_NEXT;
  const latestLaunch = proposalVariant?.latestLaunch ?? null;
  const newVersionAvailable = isUsingFedNext
    ? newFedNextVersionAvailable({ latestLaunch })
    : newMinorVersionAvailable(
        allowedTracksNonNull || [],
        sourceVariant?.compositionVersion as string,
      );

  if (graphLoading || graphLoadingError || !proposalVariant) {
    return (
      <Button isDisabled={true} variant="primary">
        Upgrade Federation Version
      </Button>
    );
  }

  return (
    <>
      <Tooltip
        isDisabled={canManageBuildConfig}
        label="You do not have permission to manage the build configuration/federation version"
      >
        <Button
          isDisabled={!canManageBuildConfig}
          variant="primary"
          as={Link}
          to={setOverlayVisibleLocation({
            location,
            overlayName: Config.modals.configureBuildPipeline,
          })}
        >
          Upgrade Federation Version
        </Button>
      </Tooltip>
      <LazyUrlModal name={Config.modals.configureBuildPipeline}>
        {({ closeModal, isOpen }) => (
          <ConfigureBuildPipelineModal
            isOpen={isOpen}
            isGraph={false}
            serviceId={graphRef.graphId}
            variant={proposalVariant}
            sourceVariantInfo={{
              name: sourceVariant?.name,
              pipelineTrack: sourceVariant?.buildConfig?.buildPipelineTrack,
            }}
            onClose={closeModal}
            refetchGraphData={refetch}
            graphType={graphType}
            allowedTracks={allowedTracksNonNull}
            defaultBuildPipelineTrack={defaultBuildPipelineTrackEnum}
            routerVersion={routerVersion}
            canUpdateFedNextVersion={
              isUsingFedNext ? newVersionAvailable : false
            }
          />
        )}
      </LazyUrlModal>
    </>
  );
};
